import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

interface AudioContextType {
  bgMusicVolume: number;
  gameSoundVolume: number;
  setBgMusicVolume: (volume: number) => void;
  setGameSoundVolume: (volume: number) => void;
  playBackgroundMusic: (gameType: string) => void;
  stopBackgroundMusic: () => void;
  playGameSound: (soundUrl: string) => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [bgMusicVolume, setBgMusicVolume] = useState<number>(() => {
    const savedSettings = localStorage.getItem('volumeSettings');
    return savedSettings ? JSON.parse(savedSettings).bgMusic : 50;
  });

  const [gameSoundVolume, setGameSoundVolume] = useState<number>(() => {
    const savedSettings = localStorage.getItem('volumeSettings');
    return savedSettings ? JSON.parse(savedSettings).gameSound : 50;
  });

  const bgMusicRef = useRef<HTMLAudioElement | null>(null);
  const gameSoundRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // Update localStorage when volume changes
    localStorage.setItem('volumeSettings', JSON.stringify({
      bgMusic: bgMusicVolume,
      gameSound: gameSoundVolume
    }));

    // Update background music volume
    if (bgMusicRef.current) {
      bgMusicRef.current.volume = bgMusicVolume / 100;
    }
  }, [bgMusicVolume, gameSoundVolume]);

  const playBackgroundMusic = (gameType: string) => {
    try {
      // Create new audio element before stopping the current one
      const newAudio = new Audio(`/${gameType}.mp3`);
      newAudio.loop = true;
      newAudio.volume = bgMusicVolume / 100;

      // Load the audio first
      newAudio.load();

      // Only after loading, stop the current audio and start the new one
      if (bgMusicRef.current) {
        bgMusicRef.current.pause();
        bgMusicRef.current = null;
      }

      // Start playing and store the reference
      newAudio.play()
        .then(() => {
          bgMusicRef.current = newAudio;
        })
        .catch(error => {
          console.error('Error playing background music:', error);
          // If autoplay is blocked, we'll keep the reference but muted
          if (error.name === 'NotAllowedError') {
            newAudio.muted = true;
            bgMusicRef.current = newAudio;
          }
        });
    } catch (error) {
      console.error('Error setting up background music:', error);
    }
  };

  const stopBackgroundMusic = () => {
    try {
      if (bgMusicRef.current) {
        // Immediately stop the audio without fade
        bgMusicRef.current.pause();
        bgMusicRef.current.currentTime = 0;
        bgMusicRef.current = null;
      }
    } catch (error) {
      console.error('Error stopping background music:', error);
      // Ensure reference is cleared
      bgMusicRef.current = null;
    }
  };

  const playGameSound = (soundUrl: string) => {
    if (gameSoundRef.current) {
      gameSoundRef.current.pause();
    }

    gameSoundRef.current = new Audio(soundUrl);
    if (gameSoundRef.current) {
      gameSoundRef.current.volume = gameSoundVolume / 100;
      gameSoundRef.current.play().catch(error => {
        console.error('Error playing game sound:', error);
      });
    }
  };

  return (
    <AudioContext.Provider
      value={{
        bgMusicVolume,
        gameSoundVolume,
        setBgMusicVolume,
        setGameSoundVolume,
        playBackgroundMusic,
        stopBackgroundMusic,
        playGameSound
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (context === undefined) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
}; 